import { Disclosure } from '@headlessui/react'
import { MenuIcon, XIcon } from '@heroicons/react/outline'
import classNames from 'classnames'
import { useScroll } from 'framer-motion'
import { NextRouter, useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { GTM_CLICK_EVENT, getEventUserType, sendGTMCustomClickEvent } from '../../helpers/gtmHelper'
import { CurrentUser } from '../../model/Common/common.interface'
import { useChangeLocale } from '../../modules/apis/common/user'
import { getRootUrl } from '../../modules/auth'
import useAppRouter, { CustomRouter } from '../../modules/hooks/common/useAppRouter'
import useHandleLogout from '../../modules/hooks/student/auth/useHandleLogout'
import LinkLocale from '../../modules/i18n/LinkLocale'
import LoginLinkA from '../../modules/i18n/LoginLinkA'
import {
  LOCALE_EN,
  LOCALE_JA,
  LOCALE_KO,
  LOCALE_VI,
  LOCALE_ZH_CHS,
  LOCALE_ZH_CHT,
  LocaleType,
} from '../../modules/i18n/config'
import { s_menu, s_pricing } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { STUDENT_BTN_ID } from '../../modules/ids/gtm/student'
import { RINGLE_LOGO_ROUND } from '../../modules/images'
import { ICON_CHEVRON_DOWN_GRAY700, ICON_CHEVRON_UP_GRAY700, ICON_GLOBE_GRAY_700 } from '../../modules/svg/svg'
import urls from '../../modules/urls'
import { IS_NOW_PROMO, SIGN_UP_MODE } from '../../modules/vars'
import Button from '../_legacy/Button'
import MobileAppDownBanner, { APP_DOWN_BANNER_LOCATION } from '../banner/MobileAppDownBanner'
import BandBannerContainer from '../common/BandBannerContainer'
import { useStudentMenu } from '../common/menu/menuUtils'
import { STUDENT_MATERIAL_SUB_TAB_KEY_DICT } from '../materials/const'
import PromoBadge from '../promotion/landing/PromoBadge'
import LeftMenu from './LeftMenu'
import RightMenu from './RightMenu'

type LandingMenuPropsType = {
  currentUser: any
  isEventBannerOpen: boolean
}

const LandingMenu = ({ currentUser, isEventBannerOpen }: LandingMenuPropsType) => {
  const router = useRouter()
  const { t, isLocaleKO, isLocaleEN } = useTranslation()

  const sendMenuClickEvent = (btnId: STUDENT_BTN_ID, btnName: string) => {
    sendGTMCustomClickEvent(
      GTM_CLICK_EVENT.CLICK_LANDING_BUTTON,
      btnId,
      btnName,
      getEventUserType(currentUser),
      'Landing',
      router.asPath,
      {
        category: 'menu',
      }
    )
  }

  const navigation: object[] = [
    {
      label: t(s_menu.landing.why_ringle),
      eventId: STUDENT_BTN_ID.CLICK_MENU_WHAT_IS_RINGLE,
      current: true,
      menuArray: [
        {
          subMenu: [
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_TUTOR,
              label: t(s_menu.landing.tutor),
              href: urls.oneOnOne.tutor,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_MATERIALS,
              label: t(s_menu.landing.lesson_materials),
              href: urls.oneOnOne.material,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_FEATURES,
              label: t(s_menu.landing.lesson_system),
              href: urls.student.landing.system,
            },
          ],
        },
      ],
    },
    {
      label: t(s_menu.landing.solution),
      eventId: STUDENT_BTN_ID.CLICK_MENU_RINGLE_FOR,
      menuArray: [
        {
          subMenuTitle: 'BY CUSTOMERS',
          subMenu: [
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_GLOBAL_PROFESSIONALS,
              label: t(s_menu.landing.office_worker),
              href: urls.student.landing.case.customer.professional,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_OVERSEAS_RESIDENTS,
              label: t(s_menu.landing.live_abroad),
              href: urls.student.landing.case.customer.liveAbroad,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_INTERNATIONAL_STUDENTS,
              label: t(s_menu.landing.study_abroad),
              href: urls.student.landing.case.customer.studyAbroad,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_BEGINNER_INTERMEDIATE,
              label: t(s_menu.landing.beginner),
              href: urls.student.landing.case.customer.beginner,
            },
          ],
        },
        {
          subMenuTitle: 'BY NEEDS',
          subMenu: [
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_BUSINESS_ENGLISH,
              label: t(s_menu.landing.business),
              href: urls.student.landing.case.purpose.business,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_INTERVIEW_PREP,
              label: t(s_menu.landing.prep_interview),
              href: urls.student.landing.case.purpose.interview,
            },
            {
              eventId: STUDENT_BTN_ID.CLICK_MENU_RESUME_ESSAY,
              label: t(s_menu.landing.correction),
              href: urls.student.landing.case.purpose.correction,
            },
          ],
        },
      ],
    },
    {
      label: t(s_menu.landing.pricing),
      eventId: STUDENT_BTN_ID.CLICK_MENU_PRICING,
      href: urls.student.landing.pricing,
      showToolTip: IS_NOW_PROMO,
    },
  ]
  const rightNav: object[] = [
    {
      label: 'Conference',
      eventId: STUDENT_BTN_ID.CLICK_MENU_CONFERENCE,
      isEvent: true,
      href: urls.student.landing.blog.index,
      none: true,
    },
    {
      label: t(s_menu.landing.b2b),
      eventId: STUDENT_BTN_ID.CLICK_MENU_BUSINESS,
      href: isLocaleKO ? null : urls.b2b.index,
      menuArray: isLocaleKO
        ? [
            {
              subMenu: [
                {
                  label: '수출 바우처',
                  href: urls.student.landing.voucher,
                  none: false,
                },
                { label: '제휴문의', href: urls.b2b.index },
              ],
            },
          ]
        : null,
    },
    {
      label: t(s_menu.portal.faq),
      eventId: STUDENT_BTN_ID.CLICK_MENU_HELP,
      href: urls.student.landing.faq,
    },
    {
      label: 'Become a Tutor',
      eventId: STUDENT_BTN_ID.CLICK_MENU_BECOME_A_TUTOR,
      href: urls.tutor.landing.home,
      none: !isLocaleEN,
    },
  ]
  const homeMenu: object[] = [{ label: 'Home', href: urls.student.landing.main(getRootUrl()), none: !currentUser }]
  const mobileNav = homeMenu.concat(navigation.concat(rightNav))

  const languageNavigation = useMemo(
    () => [
      { label: '한국어', href: '#', lang: LOCALE_KO },
      { label: 'English', href: '#', lang: LOCALE_EN },
      { label: '中文（简体）', href: '#', lang: LOCALE_ZH_CHS },
      { label: '中文（繁體）', href: '#', lang: LOCALE_ZH_CHT },
      { label: '日本語', href: '#', lang: LOCALE_JA },
      { label: 'Tiếng Việt', href: '#', lang: LOCALE_VI },
    ],
    []
  )

  return (
    <Disclosure as="nav">
      {({ open }) => (
        <>
          {/* TEMPORARILY DISABLE */}
          {/* GNB SECTION */}
          <BandBannerContainer />

          {/* LANDING MENU */}
          <div className={classNames({ 'h-screen': open }, 'mx-auto max-w-[1080px] lg:px-20')}>
            <div className="hidden h-[72px] justify-between lg:flex">
              <div className="flex">
                {/* ----LOGO---- */}
                <div className="mr-[24px] flex items-center">
                  <LinkLocale className="" href={urls.student.landing.main(getRootUrl())}>
                    <img src={RINGLE_LOGO_ROUND} className="mr-1 hidden h-[32px] w-auto lg:block" />
                  </LinkLocale>
                </div>
                {/*--- LEFT MENU ---*/}
                {/* (why ringle?, 고객.. && 하위 드롭다운 메뉴) */}
                <div className="hidden lg:flex lg:items-center lg:space-x-20">
                  <LeftMenu navigation={navigation} onClickMenuHandler={sendMenuClickEvent} />
                </div>
              </div>

              {/*--- RIGHT MENU ---*/}
              {/* (이벤트, FAQ, ...로그인, 무료체험) */}
              <div className="flex items-center">
                <RightMenu
                  rightNav={rightNav}
                  languageNavigation={languageNavigation}
                  onClickMenuHandler={sendMenuClickEvent}
                />
              </div>
            </div>
            {/*--- MOBILE MENU ---*/}
            <div className="no-scrollbar lgd:flex z-[900] w-full flex-col overflow-scroll lg:hidden">
              <MobileMenu
                mobileNav={mobileNav}
                open={open}
                currentUser={currentUser}
                isEventBannerOpen={isEventBannerOpen}
              />
            </div>
          </div>
        </>
      )}
    </Disclosure>
  )
}

type MobileMenuPropsTpye = {
  mobileNav: any
  open: any
  currentUser: any
  isEventBannerOpen: boolean
}

const locales = [
  {
    name: 'English',
    key: LOCALE_EN,
  },
  {
    name: '한국어',
    key: LOCALE_KO,
  },
  {
    name: '日本語',
    key: LOCALE_JA,
  },
  {
    name: '中文(简体)',
    key: LOCALE_ZH_CHS,
  },
  {
    name: '中文(繁體)',
    key: LOCALE_ZH_CHT,
  },
  {
    name: 'Tiếng Việt',
    key: LOCALE_VI,
  },
]

enum PageType {
  LANDING_MATERIAL_AD = 'Landing Material Ad',
  PORTAL_MATERIAL_AD = 'Portal Material Ad',
  NOT_DEFINED = 'Not Defined',
}

// {/* 모바일 메뉴 리스트 */ }
const MobileMenu = ({ mobileNav, open, currentUser, isEventBannerOpen }: MobileMenuPropsTpye) => {
  const { t, locale, updateLandingLocale } = useTranslation()
  const handleLogout = useHandleLogout()
  const { mutate } = useChangeLocale()
  const mobilePortalNav = useStudentMenu().mobileAfterLogin
  const [langOpen, setLangOpen] = useState(false)

  const mobilePanelMaxHeight = isEventBannerOpen ? 'calc(100vh - 148px)' : 'calc(100vh - 61.25px)'
  const router = useAppRouter()

  const { scrollYProgress } = useScroll()

  const pageType: PageType = useMemo(() => {
    if (router.asPath.includes('landing/contents/material/ad/')) return PageType.LANDING_MATERIAL_AD
    if (router.asPath.includes('portal/materials/ad')) return PageType.PORTAL_MATERIAL_AD
    return PageType.NOT_DEFINED
  }, [router.asPath])

  const getSignUpHref = (_pageType: PageType, _router: NextRouter | CustomRouter) => {
    return urls.student.auth.new
  }

  const getAfterLoginUrl = (_pageType: PageType, _router: NextRouter | CustomRouter) => {
    return null
  }

  const getSignUpMode = (_pageType: PageType) => {
    return null
  }

  const handleOnClickLogin = () => {
    sendGTMCustomClickEvent(
      GTM_CLICK_EVENT.CLICK_BUTTON_NEW,
      STUDENT_BTN_ID.SIGN_IN,
      '로그인',
      getEventUserType(currentUser),
      'Navigation Bar - Right Menu',
      router.asPath
    )

    router.push(urls.student.auth.signIn)
  }

  const handleOnClickSignup = (
    _router: NextRouter | CustomRouter,
    _currentUser: CurrentUser,
    _pageType: PageType,
    _scrollYProgress: number
  ) => {
    switch (_pageType) {
      case PageType.LANDING_MATERIAL_AD: {
        const type = _router.query.type ?? undefined
        sendGTMCustomClickEvent(
          GTM_CLICK_EVENT.CLICK_CONTENT_AD_SIGN_UP,
          STUDENT_BTN_ID.SIGN_UP,
          '상단 회원가입 하기 버튼 클릭',
          getEventUserType(_currentUser),
          `Landing Material Ad`,
          _router.asPath,
          {
            tab_name: STUDENT_MATERIAL_SUB_TAB_KEY_DICT[Number(type)],
            landing_type: _router.asPath.includes('landing/contents/material/ad/a') ? 'A' : 'B',
          }
        )
        break
      }

      case PageType.PORTAL_MATERIAL_AD: {
        const type = _router.query.type ?? undefined
        sendGTMCustomClickEvent(
          GTM_CLICK_EVENT.CLICK_CONTENT_AD_SIGN_UP,
          STUDENT_BTN_ID.SIGN_UP,
          '상단 회원가입 하기 버튼 클릭',
          getEventUserType(_currentUser),
          `Landing Material Ad`,
          _router.asPath,
          {
            tab_name: STUDENT_MATERIAL_SUB_TAB_KEY_DICT[Number(type)],
            landing_type: 'C',
          }
        )
        break
      }

      default: {
        sendGTMCustomClickEvent(
          GTM_CLICK_EVENT.CLICK_BUTTON_NEW,
          STUDENT_BTN_ID.SIGN_UP,
          '회원가입',
          getEventUserType(currentUser),
          'Navigation Bar - Right Menu',
          _router.asPath,
          {
            scroll_y_percentage: Math.round(_scrollYProgress * 100),
          }
        )
        break
      }
    }
  }

  const getLanguageTitle = (lang) => {
    switch (lang) {
      case 'ko':
        return '한국어'
      case 'en':
        return 'English'
      case 'zh_chs':
        return '中文（简体）'
      case 'zh_cht':
        return '中文（繁體）'
      case 'ja':
        return '日本語'
      case 'vi':
        return 'Tiếng Việt'
    }
  }

  const handleOnChangeLocale = (targetLocale: LocaleType) => {
    if (currentUser) {
      mutate({ locale: targetLocale })
    } else {
      updateLandingLocale(targetLocale)
    }
  }

  /**
   * 1. 모바일 메뉴가 열리면 젠데스크 아이콘 숨기기 처리
   * 2. 랜딩에서는 젠데스크 아이콘 숨김 처리
   */
  useEffect(() => {
    if (!open && !router.asPath.includes('/landing/home')) {
      window?.ChannelIO('hideChannelButton')
      return () => window?.ChannelIO('showChannelButton')
    }
  }, [open])

  const isSignUpPage = useMemo(
    () =>
      router.asPath.includes('/auth/new') ||
      router.asPath.includes('/auth/sign-up') ||
      router.asPath.includes('/auth/additional-info'),
    [router.asPath]
  )

  return (
    <>
      {/* 모바일 상단 헤더 */}
      <div className="flex flex-1 items-center py-[13px] px-[20px]">
        <div className="flex flex-1 justify-between">
          <div className="flex items-center justify-center">
            <LinkLocale href={urls.student.landing.main(getRootUrl())}>
              <img src={RINGLE_LOGO_ROUND} className="block h-[31px] w-auto lg:hidden" />
            </LinkLocale>
          </div>
          <div className="flex flex-row self-end">
            {/* 로그인 한 유저 - 구매하기 버튼 */}
            {currentUser && (
              <LinkLocale href={urls.student.portal.pricing.index}>
                <Button primary sm textH5 type="button">
                  {t(s_pricing.buy_credit)}
                </Button>
              </LinkLocale>
            )}
            {/* 비로그인 유저 - 가입 페이지 쪽 - 로그인 버튼 */}
            {!currentUser && isSignUpPage && (
              <Button
                px20
                py8
                type="button"
                clx="relative items-center px-6 py-4 shadow-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 bg-purple-500 text-white text-h5 lg:hidden rounded-full"
                onClick={() => handleOnClickLogin()}
              >
                {t(s_menu.landing.signin)}
              </Button>
            )}
            {/* 비로그인 유저 - 타 페이지 - 회원가입 버튼 */}
            {!currentUser && !isSignUpPage && (
              <LoginLinkA
                href={getSignUpHref(pageType, router)}
                afterLogin={getAfterLoginUrl(pageType, router)}
                mode={getSignUpMode(pageType)}
              >
                <Button
                  px20
                  py8
                  type="button"
                  clx="relative  items-center px-6 py-4 shadow-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 bg-purple-500 text-white text-h5 lg:hidden rounded-full"
                  onClick={() => handleOnClickSignup(router, currentUser, pageType, scrollYProgress.get())}
                >
                  {t(s_menu.landing.signup)}
                </Button>
              </LoginLinkA>
            )}
          </div>
        </div>
        {/* 모바일 닫기 버튼 */}
        <Disclosure.Button className="inline-flex items-center justify-self-end rounded-md pl-24 text-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
          <span className="sr-only">Open main menu</span>
          {open ? (
            <XIcon className="z-50 block h-20 w-20" aria-hidden="true" />
          ) : (
            <MenuIcon className="block h-20 w-20" aria-hidden="true" />
          )}
        </Disclosure.Button>
      </div>

      <div style={{ maxHeight: mobilePanelMaxHeight }} className="-ml-2 overflow-y-scroll lg:hidden">
        {/*  모바일 panel */}
        <Disclosure.Panel>
          <div className="text-h4 px-[20px] font-medium text-gray-700">
            {/* 로그인/로그아웃 */}
            {currentUser && (
              <>
                {/* welCOME */}
                <div className="flex items-center justify-between py-[8px]">
                  <LinkLocale href={mobilePortalNav[0].href}>{mobilePortalNav[0].label}</LinkLocale>
                </div>
                {/* 구매 */}
                {mobilePortalNav
                  .filter((item, i) => item.none != true)
                  .slice(1)
                  .map((item, i) => (
                    <div key={i} className="text-h4 mr-[4px] py-[8px]">
                      <LinkLocale href={item.href}>
                        {item.label}
                        {/* 모바일 Promo 강조 뱃지 표시
                          - 로그인 이전 : Pricing 메뉴
                          - 로그인 이후 : 구매 메뉴
                        */}

                        {item.showToolTip && <PromoBadge />}
                      </LinkLocale>
                    </div>
                  ))}
              </>
            )}
            <div className="flex items-center justify-between py-[8px]">
              {currentUser ? (
                <a onClick={handleLogout}>{t(s_menu.mypage.sign_out)}</a>
              ) : (
                <LoginLinkA
                  href={urls.student.auth.signIn}
                  afterLogin={pageType !== PageType.NOT_DEFINED ? router.asPath : null}
                  mode={SIGN_UP_MODE.SIGN_IN}
                >
                  {t(s_menu.landing.signin)}
                </LoginLinkA>
              )}
            </div>
          </div>
          {currentUser && (
            <div className="py-[8px] px-[20px]">
              <hr />
            </div>
          )}

          <div className="right-[0px] bg-white pb-32 lg:hidden">
            {/* 로그인/로그아웃 */}

            {/* 하단 랜딩 & 포털 공통 메뉴 */}
            <>
              {mobileNav
                .filter((e) => !e.none)
                .map((item, i) => (
                  <Disclosure key={i}>
                    {({ open }) => (
                      <>
                        <div>
                          <div className="relative">
                            <>
                              {
                                <Disclosure.Button className="flex w-full items-center justify-start bg-white px-20 py-8 text-left focus:text-purple-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2">
                                  <div
                                    className={classNames(
                                      `${item.isEvent ? 'text-red-500' : 'text-gray-700'}`,
                                      'text-h4 mr-[8px] font-medium'
                                    )}
                                  >
                                    {item?.href ? (
                                      item.type === '_blank' ? (
                                        <a href={item.href} target={item.type} rel="noreferrer">
                                          {item.label}
                                        </a>
                                      ) : (
                                        <LinkLocale href={item?.href}>
                                          {item.label}
                                          {/* 모바일 Promo 강조 뱃지 표시
                                       - 로그인 이전 : Pricing 메뉴
                                       - 로그인 이후 : 구매 메뉴
                                       */}
                                          {item.showToolTip && <PromoBadge />}
                                        </LinkLocale>
                                      )
                                    ) : (
                                      item?.label
                                    )}
                                  </div>

                                  {item?.menuArray?.length > 0 &&
                                    (open ? (
                                      <img src={ICON_CHEVRON_UP_GRAY700} />
                                    ) : (
                                      <img src={ICON_CHEVRON_DOWN_GRAY700} />
                                    ))}
                                </Disclosure.Button>
                              }
                              {item?.menuArray?.length > 0 && (
                                <Disclosure.Panel className="relative">
                                  <div className="my-[8px] flex flex-col bg-gray-50 px-[20px] pt-[6px] pb-[16px]">
                                    {item.menuArray.map((side, i) => (
                                      <div key={i} className="w-full px-[20px]">
                                        {side?.subMenuTitle && (
                                          <div className="text-h6 py-[8px] text-gray-700">{side.subMenuTitle}</div>
                                        )}
                                        {side.subMenu?.map((item) => (
                                          <div
                                            key={item.label}
                                            className="text-h5 py-[8px] px-[4px] font-medium text-gray-700"
                                          >
                                            <LinkLocale
                                              href={item.href}
                                              className={classNames('block hover:text-purple-500')}
                                            >
                                              {item.label}
                                            </LinkLocale>
                                          </div>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                </Disclosure.Panel>
                              )}
                            </>
                          </div>
                        </div>
                      </>
                    )}
                  </Disclosure>
                ))}
              {pageType === PageType.NOT_DEFINED && (
                <div className="flex flex-row items-center px-[20px] py-[8px]">
                  <div className="flex w-full justify-between">
                    <div className="flex flex-row items-center">
                      <img src={ICON_GLOBE_GRAY_700} className="mr-[5px]" />
                      <div
                        onClick={() => setLangOpen((prev) => !prev)}
                        className="text-h4 flex flex-row items-center font-medium text-gray-700"
                      >
                        {t(s_menu.change_language)}
                        <div className="ml-[8px]">
                          {langOpen ? <img src={ICON_CHEVRON_UP_GRAY700} /> : <img src={ICON_CHEVRON_DOWN_GRAY700} />}
                        </div>
                      </div>
                    </div>
                    <div className="text-[14px] font-medium text-purple-500">
                      {langOpen || getLanguageTitle(router.query.lang)}
                    </div>
                  </div>
                </div>
              )}
              {langOpen && (
                <div className={classNames({ 'bg-gray-50': langOpen }, 'mt-[8px] h-full px-[20px]')}>
                  <div className="ml-[24px] pt-[6px] pb-[16px]">
                    {locales.map((item) => {
                      return (
                        <>
                          <div
                            onClick={() => handleOnChangeLocale(item.key)}
                            className={classNames(
                              { 'text-purple-500': item.key == router.query.lang },
                              'py-[8px] text-[14px] font-medium text-gray-700'
                            )}
                          >
                            {item.name}
                          </div>
                        </>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          </div>

          {/* 모웹 앱 다운 유도 배너 */}
          <MobileAppDownBanner
            bannerLocation={
              router.asPath.includes('/landing/home')
                ? APP_DOWN_BANNER_LOCATION.LANDING_MENU
                : APP_DOWN_BANNER_LOCATION.PORTAL_MENU
            }
          />
        </Disclosure.Panel>
      </div>
    </>
  )
}

export default LandingMenu
